import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  Typography,
} from '@mui/material';
import { useMytaStreams } from './MytaStreamsProvider';
import {
  AFK_BODY,
  AFK_CONFIRM,
  AFK_FOOTER,
  AFK_TITLE,
  sessionEndTitle,
  afkFractionLeft,
  afkSecondsLeft,
  describeSessionEnd,
} from './afkWatch';

// The presence check, and the few seconds after a visit ends. Mounted by PixelStreamingWrapper next to
// SpotNoticeBanner so it sits over the stream AND over the loading cinema: a prompt the loader covers is a
// prompt nobody can answer, while the countdown behind it keeps running.
//
// Not dismissible by backdrop or Escape. The rule is "if nobody answers within a minute", and a click that
// lands anywhere on a darkened screen is not an answer — only the button is.
const AfkPrompt = () => {
  const { afkPrompt, confirmPresence, sessionEnd } = useMytaStreams();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!afkPrompt) {
      return undefined;
    }
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(timer);
  }, [afkPrompt]);

  // The end screen wins over the question: once the visit is over the countdown is spent and the page is
  // already on its way to the events list, so the prompt must not still be on screen offering to save it.
  if (sessionEnd) {
    return (
      <Dialog
        open
        disableEscapeKeyDown
        maxWidth="xs"
        fullWidth
        sx={{ zIndex: 1500 }}
        data-testid="myta-session-ended"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {sessionEndTitle(sessionEnd.cause)}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {describeSessionEnd(sessionEnd.cause)}
          </DialogContentText>
        </DialogContent>
      </Dialog>
    );
  }

  if (!afkPrompt) {
    return null;
  }

  const secondsLeft = afkSecondsLeft(afkPrompt, now);

  return (
    <Dialog
      open
      disableEscapeKeyDown
      maxWidth="xs"
      fullWidth
      sx={{ zIndex: 1500 }}
      data-testid="myta-afk-prompt"
    >
      <DialogTitle sx={{ fontWeight: 700 }}>{AFK_TITLE}</DialogTitle>
      <DialogContent>
        <DialogContentText>{AFK_BODY}</DialogContentText>
        <DialogContentText sx={{ mt: 1.5 }}>{AFK_FOOTER}</DialogContentText>
        <Box sx={{ mt: 2.5 }}>
          <LinearProgress
            variant="determinate"
            value={afkFractionLeft(afkPrompt, now) * 100}
            color={secondsLeft <= 10 ? 'error' : 'primary'}
            sx={{ height: 6, borderRadius: 3 }}
          />
          <Typography
            variant="body2"
            sx={{ mt: 1, fontWeight: 600, opacity: 0.8 }}
            data-testid="myta-afk-countdown"
          >
            Closing in {secondsLeft} s
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={confirmPresence}
          data-testid="myta-afk-confirm"
        >
          {AFK_CONFIRM}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AfkPrompt;
